/* -----------------------------------------------  Dom Types ----------------------------------------------- */
import {PageViewport} from 'ngx-extended-pdf-viewer';

export type IDiemnsions<T extends number | string = number> = {
	width: T;
	height: T;
};

export type IPosition<T extends number | string = number> = {
	top: T;
	left: T;
};

export type IRect<T extends number | string = number> = IDiemnsions<T> & IPosition<T>;

/* -----------------------------------------------  Dom Helpers ----------------------------------------------- */

/**
 * Get relative rect of element in container in % units
 * @param element
 * @param container
 * @param keepAspectRatio
 */
export function getRelativeRect(element: HTMLElement | IRect, container: HTMLElement, keepAspectRatio: boolean): IRect {
	const elementBoundingRect = element instanceof HTMLElement ? element.getBoundingClientRect() : element;
	const containerBoundingRect = container.getBoundingClientRect();

	const elementRect = {
		left: elementBoundingRect.left,
		top: elementBoundingRect.top,
		width: elementBoundingRect.width,
		height: elementBoundingRect.height,
	};

	if (keepAspectRatio) {
		if (elementRect.width > elementRect.height) {
			elementRect.height = elementRect.width;
		} else {
			elementRect.width = elementRect.height;
		}
	}

	const relativeX = ((elementRect.left - containerBoundingRect.left) / containerBoundingRect.width) * 100;
	const relativeY = ((elementRect.top - containerBoundingRect.top) / containerBoundingRect.height) * 100;

	const relativeWidth = (elementRect.width / containerBoundingRect.width) * 100;
	const relativeHeight = (elementRect.height / containerBoundingRect.height) * 100;

	return {
		left: relativeX,
		top: relativeY,
		width: relativeWidth,
		height: relativeHeight,
	};
}

/**
 * Adapt field rect to page -	set field rect to page bounds if it is out of bounds
 * @param fieldPercentRect
 * @param fieldPage
 * @param pagesCount
 */
export function adaptFieldToPage(fieldPercentRect: IRect, fieldPage: number, pagesCount: number): IRect {
	if (fieldPage == 1 && fieldPercentRect.top < 0) fieldPercentRect.top = 0;
	if (fieldPercentRect.left < 0) fieldPercentRect.left = 0;
	if (fieldPercentRect.left + fieldPercentRect.width > 100) fieldPercentRect.left = 100 - fieldPercentRect.width;
	if (fieldPage == pagesCount && fieldPercentRect.top + fieldPercentRect.height > 100) fieldPercentRect.top = 100 - fieldPercentRect.height;

	return fieldPercentRect;
}

/**
 * Convert field rect from % units to px units
 * @param fieldRect
 * @param viewport
 */
export function getRelativeRectPx(fieldRect: IRect, viewport: PageViewport): IRect {
	const pageWidth = viewport.width;
	const pageHeight = viewport.height;

	return {
		left: (fieldRect.left / 100) * pageWidth,
		top: pageHeight - (fieldRect.top / 100) * pageHeight,
		width: (fieldRect.width / 100) * pageWidth,
		height: (fieldRect.height / 100) * pageHeight,
	};
}

export function getElementScreenPosition(element: HTMLElement): {left: number; top: number} {
	const elementBoundingRect = element.getBoundingClientRect();
	return {
		left: elementBoundingRect.left,
		top: elementBoundingRect.top,
	};
}

export function generateMouseEvent(element: HTMLElement): MouseEvent {
	const elementBoundingRect = element.getBoundingClientRect();
	return new MouseEvent('click', {
		clientX: elementBoundingRect.left + elementBoundingRect.width / 2,
		clientY: elementBoundingRect.top + elementBoundingRect.height / 2,
		bubbles: true,
		cancelable: true,
		view: window,
	});
}
